import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { syncNativePush, stopNativePush } from './nativePush';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;
  const isAdmin = user?.is_admin || false;
  const isParentSession = user?.session_type === 'parent';
  const isStudentSession = user?.session_type === 'student';

  // On mount: check localStorage for token, validate with /auth/me
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((res) => {
          setUser(res.data.user);
        })
        .catch((err) => {
          // Rate limits, server errors and network failures do not invalidate a JWT.
          if ([401, 403, 404].includes(err.response?.status)) {
            localStorage.removeItem('token');
          }
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoading) syncNativePush(user, localStorage.getItem('token'));
  }, [user, isLoading]);

  const login = async (credentials) => {
    const res = await axios.post(`${API_URL}/auth/login`, credentials);
    const { token, user: userData } = res.data;
    localStorage.setItem('token', token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const res = await axios.post(`${API_URL}/auth/register`, data);
    const { token, user: userData } = res.data;
    localStorage.setItem('token', token);
    setUser(userData);
    return userData;
  };

  const requestPasswordReset = async (phone) => {
    const res = await axios.post(`${API_URL}/auth/request-reset`, { phone });
    return res.data;
  };

  const resetPassword = async (reset_token, reset_code, new_password) => {
    const res = await axios.post(`${API_URL}/auth/reset-password`, {
      reset_token,
      reset_code,
      new_password,
    });
    return res.data;
  };

  const logout = () => {
    stopNativePush();
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isAdmin, isLoading, isParentSession, isStudentSession, login, register, requestPasswordReset, resetPassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
